import React from 'react'
import { format, parseISO } from 'date-fns'
import {
  Spread,
  Stack,
  Text,
  Badge,
  Box,
  Divider,
  Skeleton,
  TextLinkButton
} from '@machobear/design-system'
import { vars } from '@machobear/design-system/css'
import { formatInTimeZone } from 'date-fns-tz'
import { useTranslation } from 'react-i18next'
import { type SponsoredListingDetail } from '@/features/SponsoredListing/types'
import NoListinsIllustration from './illustration/no-listings-illustration.svg?react'
import { BADGE_TONE_MAP, TIME_TRANSLATION_MAP } from '.'

const ListingSponsoredList = ({
  data,
  isLoading,
  handleBoostListing
}: {
  data: SponsoredListingDetail[]
  isLoading: boolean
  handleBoostListing: () => void
}) => {
  const { t, i18n } = useTranslation()

  if (isLoading) {
    return Array.from({ length: 3 }, () => ({})).map((__, index) => (
      <ListingSponsoredListWrapper index={index}>
        <Stack space="xsmall">
          <Spread space="xsmall" alignY="center">
            <Skeleton.Shape height={24} width={240} />
            <Skeleton.Shape height={16} width={40} />
          </Spread>
          <Skeleton.Shape height={16} width={120} />
          <Skeleton.Shape height={24} width={240} />
        </Stack>
      </ListingSponsoredListWrapper>
    ))
  }

  if (data.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        style={{ height: 'calc(100vh - 101px)' }}
      >
        <Stack space="large" align="center">
          <NoListinsIllustration />
          <Text weight="medium">Boost your listing to get started</Text>
          <TextLinkButton onClick={handleBoostListing}>
            {t('common.sponsor', 'Boost listing')}
          </TextLinkButton>
        </Stack>
      </Box>
    )
  }

  return data.map((listing, index) => {
    const { planned_start_date, planned_end_date, user_timezone, state_machine, plan_name } =
      listing

    const formatDate = (date: string) =>
      format(parseISO(date), 'MMM d, yyyy', {
        locale: TIME_TRANSLATION_MAP[i18n?.language as keyof typeof TIME_TRANSLATION_MAP]
      })
    const activeStartDate = formatDate(planned_start_date)
    const activeEndDate = formatDate(planned_end_date)

    const formattedTime = formatInTimeZone(
      parseISO(planned_start_date),
      user_timezone,
      'h:mm aa zzz',
      { locale: TIME_TRANSLATION_MAP[i18n.language as keyof typeof TIME_TRANSLATION_MAP] }
    )
    const displayText =
      state_machine === 'open'
        ? t('l.listing.dashboard.upcoming.badge.title', 'Upcoming')
        : state_machine === 'active'
          ? t('l.listing.dashboard.active.badge.title', 'Active')
          : t('l.listing.dashboard.history.badge.title', 'History')

    const duration = plan_name?.replace('-days-sponsored-listing-package', '')
    const days =
      duration === 'seven'
        ? t('l.dashboard.sponsored_listings.duration.week')
        : duration === 'fourteen'
          ? t('l.dashboard.sponsored_listings.duration.two_weeks', '14 Days')
          : t('l.dashboard.sponsored_listings.duration.month', '28 Days')

    return (
      <Box key={listing.id}>
        <Box
          paddingTop="small"
          paddingBottom="medium"
          paddingX="small"
          style={
            index === 0
              ? { borderTop: `1px solid ${vars.borderColor.neutralLight}` }
              : index === data.length - 1
                ? { marginBottom: '88px' }
                : {}
          }
        >
          <Stack space="xsmall">
            <Spread space="xsmall" alignY="center">
              <Text weight="medium">
                {activeStartDate} - {activeEndDate}
              </Text>

              <Badge tone={BADGE_TONE_MAP[state_machine as keyof typeof BADGE_TONE_MAP]}>
                {displayText}
              </Badge>
            </Spread>

            <Text size="small" tone="secondary">
              {formattedTime}
            </Text>

            <Text
              size="small"
              tone="secondary"
            >{`${days} ${t('common.sponsored_listings', 'Sponsored Listing')}`}</Text>
          </Stack>
        </Box>
        <Divider />
      </Box>
    )
  })
}

function ListingSponsoredListWrapper({
  index,
  children
}: {
  index: number
  children: React.ReactNode
}) {
  return (
    <>
      <Box
        paddingTop="small"
        paddingBottom="medium"
        paddingX="small"
        style={index === 0 ? { borderTop: `1px solid ${vars.borderColor.neutralLight}` } : {}}
      >
        {children}
      </Box>
      <Divider />
    </>
  )
}

export default ListingSponsoredList
